import type { CSSObject } from '@emotion/react'
import { Color, Size, Spacing } from '@opallabs/gem'
import type { StyleProps } from './types'

const background = ({ overSpent, theme }: StyleProps): CSSObject => ({
  background: Color[theme].Surface.N400,
  alignItems: 'center',
  borderWidth: 0,

  '@media (prefers-contrast: more)': {
    background:
      overSpent && theme === 'light'
        ? Color[theme].Text.Warning
        : Color[theme].Text.Default,
  },
})
const fill = ({ overSpent, theme }: StyleProps): CSSObject => {
  // It doesn't seem like `valueOutsideRange` is being applied directly in the
  // browser, so handle it here, too.
  const colorRules: CSSObject = overSpent
    ? valueOutsideRange({ overSpent, theme })
    : valueWithinRange({ overSpent, theme })

  return {
    ...colorRules,
    blockSize: Spacing.XS,
    // Gecko and WebKit don't apply any rounding by default. Blink has nice
    // behavior that clips the fill's path to match `<meter>`'s
    // `border-radius`, so if the meter is at 50% only the start will be
    // rounded. Rather than try to approximate that for Gecko and WebKit by
    // calculating percentages, just make the fill always rounded.
    borderRadius: Size.Rounding,
  }
}
const valueWithinRange = ({ theme }: StyleProps): CSSObject => ({
  background: Color[theme].Surface.A200,

  '@media (prefers-contrast: more)': {
    background: Color[theme].Text.InvertedDefault,
  },
})

const valueOutsideRange = ({ theme }: StyleProps): CSSObject => ({
  background: Color[theme].Surface.R100,

  '@media (prefers-contrast: more)': {
    background: Color[theme].Text.Warning,
  },
})

/**
 * @note Styling `<meter>` is complicated. There's not a standard set of
 *   selectors for the shadow DOM nodes, and Gecko applies some properties to
 *   `<meter>` that Blink and WebKit apply to the shadow DOM. Blink also has a
 *   different default appearance (with rounded ends) than Gecko and WebKit.
 *   The whole thing is kind of a mess. The styles in this file intentionally
 *   *don't* reset the `appearance`, because that brings in an entire other
 *   mess.
 */
export const container = ({ overSpent, theme }: StyleProps): CSSObject => ({
  blockSize: Spacing.S,
  borderRadius: Size.Rounding,
  boxSizing: 'border-box',
  display: 'flex',
  flexGrow: 1,
  paddingInline: Spacing.XS / 2,

  // Gecko
  // @see https://browserstrangeness.bitbucket.io/css_hacks.html#firefox
  '@supports selector(:-moz-is-html)': {
    '&': {
      paddingBlock: Spacing.XS / 2,
    },
  },
  // WebKit
  // @see https://browserstrangeness.bitbucket.io/css_hacks.html#safari
  '@media not all and (min-resolution:.001dpcm)': {
    '@media': {
      '&': {
        paddingBlock: Spacing.XS / 2,
      },
    },
  },

  // Gecko
  ...background({ overSpent, theme }),
  // Blink, WebKit
  '&::-webkit-meter-bar': {
    ...background({ overSpent, theme }),
  },

  // Gecko
  '&:-moz-meter-optimum::-moz-meter-bar': {
    ...fill({ overSpent, theme }),
  },
  // Blink, WebKit
  '&::-webkit-meter-optimum-value': {
    ...fill({ overSpent, theme }),
  },

  // Gecko
  '&:-moz-meter-sub-sub-optimum::-moz-meter-bar': {
    ...valueOutsideRange({ overSpent, theme }),
  },
  // Blink, WebKit
  '&::-webkit-meter-even-less-good-value': {
    ...valueOutsideRange({ overSpent, theme }),
  },
})
